import { useRef, useCallback, useEffect, useState } from 'react';

interface UseDragDropOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onDrop: (files: FileList) => void;
  enabled?: boolean;
}

function hasFileDragPayload(event: React.DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('Files');
}

export function useDragDrop({ containerRef, onDrop, enabled = true }: UseDragDropOptions) {
  const dragCounter = useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  const reset = useCallback(() => {
    dragCounter.current = 0;
    setIsDraggingFiles(false);
  }, []);

  useEffect(() => {
    if (!enabled) reset();
  }, [enabled, reset]);

  useEffect(() => {
    // Cancellation can arrive before activation effects run. Keep these listeners
    // ready for the hook's lifetime rather than waiting for the overlay to appear.
    const events = ['drop', 'dragend', 'blur'];
    for (const event of events) window.addEventListener(event, reset);
    return () => {
      for (const event of events) window.removeEventListener(event, reset);
    };
  }, [reset]);

  useEffect(() => {
    if (!isDraggingFiles) return;
    const container = containerRef.current;
    container?.classList.add('drag-active');
    return () => container?.classList.remove('drag-active');
  }, [containerRef, isDraggingFiles]);

  return {
    handleDragEnter(e: React.DragEvent) {
      if (!hasFileDragPayload(e)) return;
      e.preventDefault();
      if (enabled && ++dragCounter.current === 1) setIsDraggingFiles(true);
    },
    handleDragLeave(e: React.DragEvent) {
      // Browsers may hide transfer metadata on leave; balance the tracked entries instead.
      if (!dragCounter.current) return;
      e.preventDefault();
      if (--dragCounter.current === 0) reset();
    },
    handleDragOver(e: React.DragEvent) {
      if (!hasFileDragPayload(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = enabled ? 'copy' : 'none';
    },
    handleDrop(e: React.DragEvent) {
      if (!hasFileDragPayload(e)) return;
      // Even disabled uploads must prevent the browser from navigating to the file.
      e.preventDefault();
      reset();
      if (enabled && e.dataTransfer.files.length) onDrop(e.dataTransfer.files);
    },
    isDraggingFiles,
  };
}
